// src/views/incidencias/IncidenciasListado.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../apiConfig";

// 📌 Exportar Excel
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// 📌 Exportar PDF
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function IncidenciasListado() {
  const [incidencias, setIncidencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ==============================
  // CARGAR INCIDENCIAS
  // ==============================
  const cargarIncidencias = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/incidencias`);
      setIncidencias(res.data || []);
    } catch (err) {
      console.error("ERROR GET INCIDENCIAS:", err);
      setError("No se pudieron cargar las incidencias.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarIncidencias();
  }, []);

  // ==============================
  // ACTUALIZAR ESTADO INCIDENCIA
  // ==============================
  const actualizarEstado = async (idIncidencia, nuevoEstado) => {
    if (!idIncidencia) {
      alert("Error: incidencia sin ID válido.");
      return;
    }

    const confirmacion = window.confirm(
      "¿Confirmas marcar esta incidencia como SOLUCIONADA?"
    );
    if (!confirmacion) return;

    try {
      await axios.put(
        `${API_BASE}/incidencias/estado`,
        { id: idIncidencia, nuevoEstado },
        { headers: { "Content-Type": "application/json" } }
      );

      alert("Incidencia marcada como SOLUCIONADA.");
      cargarIncidencias();
    } catch (error) {
      console.error("ERROR UPDATE:", error);
      alert("Error al actualizar el estado.");
    }
  };

  // ==============================
  // EXPORTAR EXCEL
  // ==============================
  const exportarExcel = () => {
    const data = incidencias.map((i) => ({
      Equipo: i.equipo,
      Serie: i.serie,
      Ubicación: `${i.oficina} - ${i.municipio}`,
      Estado: i.estadoNuevo,
      Riesgo: i.nivelRiesgo,
      Refacciones: i.refacciones?.join(", ") || "Sin refacciones",
      Reemplazo: i.reemplazo ? "Requiere" : "No requiere",
      Fecha: formatearFecha(i.fechaReporte),
      Reportado_por: i.reportadoPor,
    }));

    const hoja = XLSX.utils.json_to_sheet(data);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Incidencias");

    const excelBuffer = XLSX.write(libro, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "reporte_incidencias.xlsx");
  };

  // ==============================
  // EXPORTAR PDF
  // ==============================
  const exportarPDF = () => {
    const doc = new jsPDF("landscape");
    doc.setFontSize(16);
    doc.text("Reporte de Incidencias de Equipos", 14, 15);

    const filas = incidencias.map((i) => [
      i.equipo,
      i.serie,
      `${i.oficina} - ${i.municipio}`,
      i.estadoNuevo,
      i.nivelRiesgo,
      i.refacciones?.join(", ") || "Sin refacciones",
      i.reemplazo ? "Requiere" : "No requiere",
      formatearFecha(i.fechaReporte),
      i.reportadoPor,
    ]);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "Equipo",
          "Serie",
          "Ubicación",
          "Estado",
          "Riesgo",
          "Refacciones",
          "Reemplazo",
          "Fecha",
          "Reportado por",
        ],
      ],
      body: filas,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [30, 41, 59] }, // slate-900
    });

    doc.save("reporte_incidencias.pdf");
  };

  // ==============================
  // FORMATEAR FECHA
  // ==============================
  function formatearFecha(fecha) {
    if (!fecha) return "N/D";
    const d = new Date(fecha);
    return d.toLocaleString("es-MX", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // ==============================
  // RENDER UI (tu diseño exacto)
  // ==============================
  return (
    <div className="max-w-7xl mx-auto px-4">

      {/* HEADER */}
      <h1 className="text-3xl font-bold text-slate-800">Incidencias de Equipos</h1>
      <p className="text-sm text-slate-500 mb-6">
        Panel de alertas, refacciones y equipos que requieren atención inmediata.
      </p>

      {/* BOTONES */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={exportarExcel}
          className="px-5 py-2 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700"
        >
          Descargar Excel
        </button>

        <button
          onClick={exportarPDF}
          className="px-5 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700"
        >
          Descargar PDF
        </button>

        <button
          onClick={cargarIncidencias}
          className="px-5 py-2 rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-700"
        >
          Actualizar
        </button>
      </div>

      {/* TABLA (tu diseño exacto) */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="px-4 py-3 text-left">Equipo</th>
                <th className="px-4 py-3 text-left">Serie</th>
                <th className="px-4 py-3 text-left">Ubicación</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3 text-left">Riesgo</th>
                <th className="px-4 py-3 text-left">Refacciones</th>
                <th className="px-4 py-3 text-left">Reemplazo</th>
                <th className="px-4 py-3 text-left">Fecha</th>
                <th className="px-4 py-3 text-left">Reportado por</th>
                <th className="px-4 py-3 text-center">Acción</th>
              </tr>
            </thead>

            <tbody>
              {incidencias.map((inc) => (
                <tr key={inc.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">{inc.equipo}</td>
                  <td className="px-4 py-3">{inc.serie}</td>
                  <td className="px-4 py-3">{inc.oficina} - {inc.municipio}</td>
                  <td className="px-4 py-3">{inc.estadoNuevo}</td>
                  <td className="px-4 py-3">{inc.nivelRiesgo}</td>
                  <td className="px-4 py-3">{inc.refacciones?.join(", ") || "Sin refacciones"}</td>
                  <td className="px-4 py-3">{inc.reemplazo ? "Requiere" : "No requiere"}</td>
                  <td className="px-4 py-3">{formatearFecha(inc.fechaReporte)}</td>
                  <td className="px-4 py-3">{inc.reportadoPor}</td>

                  <td className="px-4 py-3 text-center">
                    {inc.estadoNuevo !== "SOLUCIONADO" ? (
                      <button
                        onClick={() => actualizarEstado(inc.id, "SOLUCIONADO")}
                        className="px-3 py-1 bg-emerald-600 text-white rounded-lg text-xs hover:bg-emerald-700"
                      >
                        Marcar como Solucionado
                      </button>
                    ) : (
                      <span className="text-emerald-600 font-semibold">✓ Solucionado</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  );
}
